import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layout, Card, Avatar, Spin } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  Loading3QuartersOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import "../App.css";
import { ROUTES, TOKEN_KEY, UserType } from "./common";
import {
  clearPendingJobApplication,
  consumePendingJobApplication,
} from "./common/pendingJobApplication";
import { takePostLoginRedirect } from "./common/postLoginRedirect";
import { axiosInstant, endpoints } from "../api";
import { useUser } from "../context";
import { showError } from "../Notifications";

const { Header, Content } = Layout;

const categories = [
  {
    bgColor: "#87d068",
    icon: <UserOutlined />,
    name: "Recruiter",
    value: UserType.RECRUITER,
  },
  {
    bgColor: "#1890ff",
    icon: <TeamOutlined />,
    name: "Hiring Manager",
    value: UserType.MANAGER,
  },
  {
    bgColor: "#722ed1",
    icon: <SolutionOutlined />,
    name: "Candidate",
    value: UserType.CANDIDATE,
  },
];

const FinishSignUp = () => {
  const navigate = useNavigate();
  const { user, setUser, getMe } = useUser();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const initialized = useRef(false);

  function paramsToObject(entries: any) {
    const result = {};
    // @ts-ignore
    for (const [key, value] of entries) result[key] = value;
    return result;
  }

  // Only candidates can hold a job application; for any other role the parked
  // job is dropped so it cannot resurface on a later sign-in.
  const resolvePendingApplication = async (category: UserType, id: string) => {
    if (category === UserType.CANDIDATE) {
      await consumePendingJobApplication(id);
    } else {
      clearPendingJobApplication();
    }
  };

  // Send the user to whatever protected page they clicked in an email, falling
  // back to the dashboard when they just signed in normally.
  const goToDestination = () => navigate(takePostLoginRedirect() || ROUTES.DASHBOARD);

  useEffect(() => {
    // Prevent double initialization in React strict mode
    if (initialized.current) return;
    initialized.current = true;

    const errorParam = searchParams.get("error");
    if (errorParam) {
      showError(
        { response: { data: { message: decodeURIComponent(errorParam) } } },
        "Sign in failed"
      );
      navigate(ROUTES.LANDING);
      return;
    }

    const userObj = paramsToObject(searchParams.entries()) as any;
    
    // Check if we have URL params from OAuth redirect
    if (userObj.token && userObj._id) {
      // Store token first
      localStorage.setItem(TOKEN_KEY, userObj.token);
      
      // If user already has a category, redirect to dashboard
      if (userObj.category) {
        setUser(userObj);
        // They may have signed in from a shared job link, so create that
        // application before landing them on the dashboard.
        resolvePendingApplication(userObj.category, userObj._id).then(goToDestination);
        return;
      }
      
      // Set user data and userId for category selection
      setUser(userObj);
      setUserId(userObj._id);
    } else {
      // No URL params — check if we have a user in context
      const id = user?._id;
      if (id) {
        if (user?.category) {
          resolvePendingApplication(user.category, id).then(goToDestination);
          return;
        }
        setUserId(id);
      } else {
        // No token, no user — redirect to landing
        navigate(ROUTES.LANDING);
      }
    }
  }, [searchParams]);

  const handleClick = async (e: UserType) => {
    if (!userId) return;

    setLoading(true);
    try {
      await axiosInstant.put(endpoints.updateUser(userId), { category: e });
      // Fetch fresh user data so subscription is in context and the gate is enforced
      const freshUser = await getMe();
      // getMe() calls verifyUserSubscription which redirects expired recruiters to /payment
      const isExpiredRecruiter =
        freshUser?.category === UserType.RECRUITER &&
        (freshUser?.subscription?.expiredAt ?? 0) < Date.now();
      if (!isExpiredRecruiter) {
        // A candidate who arrived from a shared job link gets that job created as
        // a pending application, so it is waiting on the dashboard they land on.
        await resolvePendingApplication(e, freshUser?._id || userId);
        goToDestination();
      }
    } catch (err) {
      showError(err);
      setLoading(false);
    }
  };

  if (loading) return <Spin spinning fullscreen tip="Setting up your account..." />;
  
  if (!userId) return <Spin spinning fullscreen tip="Loading..." />;
  
  // If user already has a category, don't show the selection cards
  if (user?.category) return null;

  return (
    <Layout>
      <Header className="header">
        <div className="logo">
          <Link to="/">
            <img
              src="./chronoflow.svg"
              alt="ChronoFlow Logo"
              className="logo-img"
            />
          </Link>
        </div>
      </Header>
      <Content className="content">
        <div className="profile-cards">
          {categories.map((e) => (
            <Card
              className="profile-card"
              hoverable
              onClick={() => handleClick(e.value)}
            >
              <div className="profile-card-content">
                <Avatar
                  size={128}
                  style={{ backgroundColor: e.bgColor }}
                  icon={e.icon}
                />
                <h3>{e.name}</h3>
              </div>
            </Card>
          ))}
        </div>
      </Content>
    </Layout>
  );
};

export default FinishSignUp;
